/**
 * wishlist.js
 * LocalStorage-based wishlist with a slide-in drawer.
 * Exposes window.Wishlist for use by other scripts.
 * No external dependencies.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'manfredi_wishlist';

  /* ── Storage helpers ── */

  function getItems() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch (e) { return []; }
  }

  function saveItems(items) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }
    catch (e) { /* storage full or unavailable */ }
  }

  function isInWishlist(productId) {
    var id = String(productId);
    return getItems().some(function (item) { return String(item.id) === id; });
  }

  function addItem(product) {
    if (isInWishlist(product.id)) return;
    var items = getItems();
    items.push({
      id:     product.id,
      title:  product.title,
      handle: product.handle,
      vendor: product.vendor || '',
      price:  product.variants && product.variants[0] ? product.variants[0].price : 0,
      variantId: product.variants && product.variants[0] ? product.variants[0].id : null,
      image:  product.featured_image || (product.images && product.images[0]) || '',
      url:    '/products/' + product.handle
    });
    saveItems(items);
    emit();
  }

  function removeItem(productId) {
    var id = String(productId);
    saveItems(getItems().filter(function (item) { return String(item.id) !== id; }));
    emit();
  }

  function toggleItem(product) {
    if (isInWishlist(product.id)) { removeItem(product.id); return false; }
    addItem(product); return true;
  }

  function emit() {
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: getItems() } }));
  }

  /* Expose public API */
  window.Wishlist = { getItems: getItems, addItem: addItem, removeItem: removeItem, toggleItem: toggleItem, isInWishlist: isInWishlist };

  /* ── Money formatter ── */
  function formatMoney(cents) {
    var val = (cents / 100).toFixed(2);
    var p = val.split('.');
    p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + p.join('.');
  }

  /* ── Drawer ── */

  var drawer      = document.getElementById('wishlist-drawer');
  var body        = drawer && drawer.querySelector('[data-wl-body]');
  var emptyEl     = drawer && drawer.querySelector('[data-wl-empty]');
  var countEl     = drawer && drawer.querySelector('[data-wl-count]');
  var previousFocus = null;

  function setHeaderOffset() {
    var header = document.getElementById('site-header');
    if (!header) return;
    var bottom = header.getBoundingClientRect().bottom;
    document.documentElement.style.setProperty('--header-h', Math.max(0, bottom) + 'px');
  }
  function openDrawer() {
    if (!drawer) return;
    setHeaderOffset();  
    drawer.removeAttribute('hidden');
    drawer.classList.add('wl-drawer--open');
    document.body.style.overflow = 'hidden';
    previousFocus = document.activeElement;
    renderDrawer();
    var first = drawer.querySelector('button, [href], input, [tabindex]:not([tabindex="-1"])');
    if (first) first.focus();
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('wl-drawer--open');
    document.body.style.overflow = '';
    setTimeout(function () {
      drawer.setAttribute('hidden', '');
      if (previousFocus) previousFocus.focus();
    }, 350);
  }

  /* ── Render ── */

  function renderDrawer() {
    if (!body) return;
    var items = getItems();

    /* Count badge */
    if (countEl) {
      countEl.textContent = items.length;
      countEl.hidden = items.length === 0;
    }

    /* Empty state */
    if (emptyEl) emptyEl.hidden = items.length > 0;

    if (items.length === 0) { body.innerHTML = ''; return; }

    body.innerHTML = items.map(function (item) {
      var imgHtml = item.image
        ? '<img class="wl-card__image" src="' + item.image + '" alt="' + escHtml(item.title) + '" loading="lazy" width="72" height="72">'
        : '<div class="wl-card__image-placeholder"></div>';

      var vendorHtml = item.vendor
        ? '<p class="wl-card__vendor">' + escHtml(item.vendor) + '</p>'
        : '';

      return [
        '<div class="wl-card" data-wl-card data-product-id="' + item.id + '">',
          '<div class="wl-card__image-wrap">',
            '<a href="' + item.url + '" tabindex="-1" aria-hidden="true">' + imgHtml + '</a>',
          '</div>',
          '<div class="wl-card__info">',
            vendorHtml,
            '<a href="' + item.url + '" class="wl-card__title">' + escHtml(item.title) + '</a>',
            '<p class="wl-card__price">' + formatMoney(item.price) + '</p>',
            '<button type="button" class="wl-card__atc" data-wl-atc data-variant-id="' + item.variantId + '">',
              'Add to Cart',
            '</button>',
          '</div>',
          '<button type="button" class="wl-card__remove" data-wl-remove data-product-id="' + item.id + '" aria-label="Remove ' + escHtml(item.title) + ' from wishlist">',
            '<svg viewBox="0 0 16 16" fill="none" width="14" height="14" aria-hidden="true">',
              '<path d="M3 3L13 13M13 3L3 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
            '</svg>',
          '</button>',
        '</div>'
      ].join('');
    }).join('');
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Count badge on any [data-wl-open] trigger ── */

  function updatePageBadges() {
    var count = getItems().length;
    document.querySelectorAll('[data-wl-badge]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /* ── Heart button state ── */

  function updateHeartButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var id = btn.dataset.wishlistToggle;
      var active = isInWishlist(id);
      btn.classList.toggle('wl-btn--active', active);
      var label = btn.querySelector('[data-wl-label]');
      if (label) label.textContent = active ? 'Saved' : btn.dataset.labelDefault || 'Add to Wishlist';
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  /* ── AJAX Add to Cart from drawer ── */

  function ajaxAtc(variantId) {
    if (!variantId) return;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) { if (!r.ok) throw new Error(); return r.json(); })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:open-drawer', { bubbles: true }));
        closeDrawer();
      })
      .catch(function () {
        /* Fallback: direct cart add */
        window.location.href = '/cart/' + variantId + ':1';
      });
  }

  /* ── Get product data from page JSON ── */

  function getProductData() {
    var jsonEl = document.querySelector('[data-mpc-product-json]');
    if (!jsonEl) return null;
    try { return JSON.parse(jsonEl.textContent); }
    catch (e) { return null; }
  }

  /* ── Event delegation ── */

  document.addEventListener('click', function (e) {
    /* Open drawer triggers */
    if (e.target.closest('[data-wl-open]')) { openDrawer(); return; }

    /* Close */
    if (e.target.closest('[data-wl-close]') || (drawer && e.target === drawer.querySelector('[data-wl-backdrop]'))) {
      closeDrawer(); return;
    }

    /* Heart toggle */
    var toggleBtn = e.target.closest('[data-wishlist-toggle]');
    if (toggleBtn) {
      var product = getProductData();
      if (!product) return;
      toggleItem(product);
      updateHeartButtons();
      return;
    }

    /* Remove from drawer */
    var removeBtn = e.target.closest('[data-wl-remove]');
    if (removeBtn) {
      removeItem(removeBtn.dataset.productId);
      updateHeartButtons();
      renderDrawer();
      return;
    }

    /* ATC from drawer */
    var atcBtn = e.target.closest('[data-wl-atc]');
    if (atcBtn) { ajaxAtc(atcBtn.dataset.variantId); return; }
  });

  /* Escape to close */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer && drawer.classList.contains('wl-drawer--open')) closeDrawer();
  });

  /* Focus trap in drawer */
  drawer && drawer.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var focusable = Array.from(drawer.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])'));
    var first = focusable[0];
    var last  = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* Re-render on wishlist changes */
  document.addEventListener('wishlist:updated', function () {
    renderDrawer();
    updatePageBadges();
    updateHeartButtons();
  });

  /* Init on DOM ready */
  function init() {
    updateHeartButtons();
    updatePageBadges();
    /* Initial count badge */
    if (countEl) {
      var n = getItems().length;
      countEl.textContent = n;
      countEl.hidden = n === 0;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
